import { constants } from '@lmc/common'
import type { Home } from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit/src/general'
import * as GlobalActions from 'actions/Global'
import { getJSON, postJSON } from 'common/services/api'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { OnboardingButton, OnboardingButtonWrapper } from './OnboardingButton'
import { OnboardingHeading } from './OnboardingHeading'
import { OnboardingLayout } from './OnboardingLayout'

export type Props = {
  start: () => void
  home?: Home
}

export const StepDiscover: FC<Props> = ({ start, home }: Props) => {
  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const dispatch = useDispatch()

  const hasPro = home && home?.features?.pro?.enabled

  const [isLoading, setIsLoading] = useState(false)

  const flags = useFeatureFlags({
    flagNames: ['self_serve_flat_fee_pricing'] as const
  })?.data
  const useFlatFeePlan = flags?.self_serve_flat_fee_pricing?.enabled

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const subscribeTo = async () => {
    try {
      setIsLoading(true)
      await postJSON({
        url: `services/chargebee/subscriptions/${
          useFlatFeePlan ? 'Licence-fee-GBP-Monthly' : 'pro-monthly'
        }`
      })

      const res = await getJSON({ url: '/api/init' })

      const { home: homeRefresh } = res.data

      if (res) {
        dispatch(GlobalActions.setHomeDetails(homeRefresh))
        return
      }

      setIsLoading(false)
    } catch (error) {
      console.log(error)
    }
  }

  const isSelfServe = home?.source === 'Self-serve'

  const getButton = () => {
    if (isSelfServe) {
      return (
        <OnboardingButtonWrapper>
          <OnboardingButton
            variant="default"
            onClick={subscribeTo}
            size="large"
            isLoading={isLoading}
          >
            {t(selfServeConstants.START_PRO_TRIAL)}
          </OnboardingButton>
        </OnboardingButtonWrapper>
      )
    }

    if (hasPro) {
      return (
        <OnboardingButtonWrapper>
          <OnboardingButton
            variant="popout"
            onClick={start}
            size="large"
            isLoading={isLoading}
          >
            {t(selfServeConstants.START_SET_UP)}
          </OnboardingButton>
        </OnboardingButtonWrapper>
      )
    }

    return (
      <OnboardingButtonWrapper>
        <OnboardingButton
          isLoading={isLoading}
          variant="popout"
          size="large"
          onClick={() => window.open(constants.HUBSPOT_DISCOVERY_CALL_URL)}
        >
          {t(selfServeConstants.INTERESTED_IN)}
        </OnboardingButton>
        <ContinueWrapper onClick={start}>
          <StyledText level={1}>
            {t(selfServeConstants.CONTINUE_WITH)}
          </StyledText>
        </ContinueWrapper>
      </OnboardingButtonWrapper>
    )
  }

  return (
    <OnboardingLayout>
      <OnboardingHeading level={1}>
        {t(selfServeConstants.DISCOVER_WHAT)}
      </OnboardingHeading>
      {getButton()}
    </OnboardingLayout>
  )
}

const ContinueWrapper = styled.div`
  z-index: 10;
  cursor: pointer;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.palette.lmcPink};
  text-decoration: underline;
`
